import { FC, useEffect, useRef } from 'react';
import DrawerAtom from 'components/atoms/DrawerAtom';
import TextAtom from 'components/atoms/TextAtom';
import { COLORS } from 'constants/colors';
import DefaultButton from 'components/atoms/Button/DefaultButton';
import { AutoComplete, Checkbox, DatePicker, Form, Tag } from 'antd';
import { Moment } from 'moment';
import InputAtom from 'components/atoms/InputAtom';
import { Icon } from 'components/atoms/Icons';
import PrimaryButton from 'components/atoms/Button/PrimaryButton';
import { CheckInInitialStatesModel, CheckInStateKey } from 'hooks/useCheckInRoom/models';
import './styles.scss';
import TextLinkAtom from 'components/atoms/TextLinkAtom';

const DRAWER_ATOM_WIDTH = '480px';

export interface CheckInCreateRoomDrawerFormValues {
  room: string,
  [CheckInStateKey.EMAILS]: string[];
  [CheckInStateKey.RANGE]: Moment[];
  [CheckInStateKey.START_TIME]: Moment;
  [CheckInStateKey.END_TIME]: Moment;
  [CheckInStateKey.CARD_1]: string;
  [CheckInStateKey.CARD_2]: string;
  [CheckInStateKey.CARD_3]: string;
  [CheckInStateKey.CARD_4]: string;
  [CheckInStateKey.CARD_5]: string;
  [CheckInStateKey.CARD_6]: string;
  [CheckInStateKey.CARD_7]: string;
  [CheckInStateKey.CARD_8]: string;
  [CheckInStateKey.CARD_9]: string;
}

interface Props extends CheckInInitialStatesModel {
  addButtonCardText: string;
  addMoreCard: () => void;
  hasExtraCards: boolean;
  isVisibleAddMoreCardLink: boolean;
}

const CheckInCreateRoomDrawer: FC<Props> = ({
  drawer,
  digitalBox,
  emails,
  cardListBox,
  card1,
  card2,
  card3,
  card4,
  card5,
  card6,
  card7,
  card8,
  card9,
  endTime,
  startTime,
  rangeDate,
  addButtonCardText,
  addMoreCard,
  hasExtraCards,
  isVisibleAddMoreCardLink,
}) => {
  const emailRef = useRef<any>(null);
  const card1Ref = useRef<any>(null);
  const card2Ref = useRef<any>(null);
  const card3Ref = useRef<any>(null);
  const card4Ref = useRef<any>(null);
  const card5Ref = useRef<any>(null);
  const card6Ref = useRef<any>(null);
  const card7Ref = useRef<any>(null);
  const card8Ref = useRef<any>(null);
  const card9Ref = useRef<any>(null);

  const refs = {
    [CheckInStateKey.CARD_1]: card1Ref,
    [CheckInStateKey.CARD_2]: card2Ref,
    [CheckInStateKey.CARD_3]: card3Ref,
    [CheckInStateKey.CARD_4]: card4Ref,
    [CheckInStateKey.CARD_5]: card5Ref,
    [CheckInStateKey.CARD_6]: card6Ref,
    [CheckInStateKey.CARD_7]: card7Ref,
    [CheckInStateKey.CARD_8]: card8Ref,
    [CheckInStateKey.CARD_9]: card9Ref,
  }

  useEffect(() => {
    digitalBox.isContentVisible && emailRef.current?.focus();
  }, [digitalBox.isContentVisible]);

  useEffect(() => {
    cardListBox.isContentVisible && card1Ref.current?.focus();
  }, [cardListBox.isContentVisible]);

  return (
    <DrawerAtom
      width={DRAWER_ATOM_WIDTH}
      className={'CheckInCreateRoomDrawer__wrapper'}
      title={
        <TextAtom style={{ color: COLORS.text }}>{drawer.title}</TextAtom>
      }
      destroyOnClose={true}
      closable={!drawer.isLoading}
      maskClosable={!drawer.isLoading}
      onClose={drawer.onClose}
      placement={'right'}
      size={'large'}
      open={drawer.isVisible}
    >
      <main className={'CheckInCreateRoomDrawer__main'}>
        <div className={'CheckInCreateRoomDrawer__main-wrapper'}>
          <div className={'CheckInCreateRoomDrawer__main-wrapper-input'}>
            <div className={'CheckInCreateRoomDrawer__main-wrapper-input-label'}>{rangeDate.label} *</div>
            <Form.Item
              validateStatus={rangeDate.isError ? 'error' : undefined}
              help={rangeDate.errorMessage}
            >
              <DatePicker.RangePicker
                className={'CheckInCreateRoomDrawer__main-wrapper-input-range-picker'}
                disabledDate={rangeDate.onDisabledDate}
                format={rangeDate.formatDate}
                disabled={rangeDate.isDisabled}
                allowClear
                defaultValue={rangeDate.values as any}
                onChange={rangeDate.onChangeRange as any}
              />
            </Form.Item>
          </div>
          <div className={'CheckInCreateRoomDrawer__main-wrapper-input-time'}>
            <div className={'CheckInCreateRoomDrawer__main-wrapper-time-picker-input'}>
              <div className={'CheckInCreateRoomDrawer__main-wrapper-input-label'}>{startTime.label} *</div>
              <Form.Item
                validateStatus={startTime.isError ? 'error' : undefined}
                help={startTime.errorMessage}
              >
                <DatePicker.TimePicker
                  className={'CheckInCreateRoomDrawer__main-wrapper-input-time-picker'}
                  disabledTime={startTime.onDisabledTime}
                  format={startTime.formatTime}
                  disabled={startTime.isDisabled}
                  onChange={startTime.onChangeTime}
                  defaultValue={startTime.value || undefined}
                  allowClear
                />
              </Form.Item>
            </div>
            <div className={'CheckInCreateRoomDrawer__main-wrapper-time-picker-input'}>
              <div className={'CheckInCreateRoomDrawer__main-wrapper-input-label'}>{endTime.label} *</div>
              <Form.Item
                validateStatus={endTime.isError ? 'error' : undefined}
                help={endTime.errorMessage}
              >
                <DatePicker.TimePicker
                  className={'CheckInCreateRoomDrawer__main-wrapper-input-time-picker'}
                  disabledTime={endTime.onDisabledTime}
                  format={endTime.formatTime}
                  disabled={endTime.isDisabled}
                  onChange={endTime.onChangeTime}
                  defaultValue={endTime.value || undefined}
                  allowClear
                />
              </Form.Item>
            </div>
          </div>
          <div>
            <div className={'CheckInCreateRoomDrawer__main-wrapper-input-label'}>{drawer.keysSectionTitle}:</div>
            <div className={'CheckInCreateRoomDrawer__main-wrapper-input-description'}>{drawer.keysSectionDescription}</div>
            <div
              className={
                `CheckInCreateRoomDrawer__main-box ${digitalBox.isContentVisible && 'CheckInCreateRoomDrawer__main-box--open'}`
              }
              onClick={() => {
                !digitalBox.isContentVisible && digitalBox.onShowContent(!digitalBox.isContentVisible)
              }}
            >
              <div className={'CheckInCreateRoomDrawer__main-box-title-wrapper'}>
                <div className={'CheckInCreateRoomDrawer__main-box-title'}>{digitalBox.title}</div>
                <Checkbox
                  checked={digitalBox.isContentVisible}
                  onChange={() => digitalBox.onShowContent(!digitalBox.isContentVisible)}
                />
              </div>
              {digitalBox.isContentVisible && (
                <div>
                  <div className={'CheckInCreateRoomDrawer__main-box-label'}>{emails.label} *</div>
                  <Form.Item
                    validateStatus={emails.isError ? 'error' : undefined}
                    help={emails.errorMessage}
                  >
                    <AutoComplete
                      ref={emailRef}
                      onChange={(value) => emails.onChangeEmail(value)}
                      value={emails.value}
                      placeholder={emails.placeholder}
                      options={[{ value: emails.value }]}
                      open={emails.isValid}
                      onSelect={emails.onEmailSelected}
                      onBlur={() => emails.onEmailSelected(emails.value)}
                    />
                  </Form.Item>
                  <div className={'CheckInCreateRoomDrawer__email-tag-list'}>
                    {emails.valueList.map(emailTag => (
                      <Tag
                        className={'CheckInCreateRoomDrawer__email-tag'}
                        closable
                        onClose={(event) => {
                          event.preventDefault();
                          emails.onRemoveEmailItemList(emailTag);
                        }}
                      >
                        {emailTag}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div
              className={
                `CheckInCreateRoomDrawer__main-box ${cardListBox.isContentVisible && 'CheckInCreateRoomDrawer__main-box--open'}`
              }
              onClick={() => {
                !cardListBox.isContentVisible && (
                  cardListBox.onShowContent(!cardListBox.isContentVisible)
                )
              }}
            >
              <div className={'CheckInCreateRoomDrawer__main-box-title-wrapper'}>
                <div className={'CheckInCreateRoomDrawer__main-box-title'}>{cardListBox.title}</div>
                <Checkbox
                  checked={cardListBox.isContentVisible}
                  onChange={() => cardListBox.onShowContent(!cardListBox.isContentVisible)}
                />
              </div>
              {cardListBox.isContentVisible && (
                <>
                  <div>
                    {[card1, card2, card3].map((card, key) => {
                      const label = `${card.label} ${card.isRequired ? '*' : ''}`;
                      return (
                        <div key={key}>
                          <div className={'CheckInCreateRoomDrawer__main-box-label'}>{label}</div>
                          <Form.Item
                            validateStatus={card.isError ? 'error' : undefined}
                            help={card.errorMessage}
                          >
                            <InputAtom
                              style={{ width: '85%' }}
                              ref={refs[card.key]}
                              onChange={(event) => card.onChangeValue(event.target.value)}
                              value={card.value}
                              placeholder={card.placeholder}
                            />
                            <DefaultButton
                              icon={<Icon.QR />}
                              onClick={() => drawer.onShowQr(card.key)}
                            />
                          </Form.Item>
                        </div>
                      )
                    })}
                    {[
                      card4,
                      card5,
                      card6,
                      card7,
                      card8,
                      card9
                    ].map((card, key) => {
                      return (
                        <div key={key}>
                          {card.isVisible && (
                            <>
                              <div className={'CheckInCreateRoomDrawer__main-box-label'}>{card.label}</div>
                              <Form.Item
                                validateStatus={card.isError ? 'error' : undefined}
                                help={card.errorMessage}
                              >
                                <InputAtom
                                  style={{ width: '85%' }}
                                  ref={refs[card.key]}
                                  onChange={(event) => card.onChangeValue(event.target.value)}
                                  value={card.value}
                                  placeholder={card.placeholder} />
                                <DefaultButton
                                  icon={<Icon.QR />}
                                  onClick={() => drawer.onShowQr(card.key)} />
                              </Form.Item>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {hasExtraCards && isVisibleAddMoreCardLink && (
                    <TextLinkAtom onClick={addMoreCard}>{addButtonCardText}</TextLinkAtom>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className='CheckInCreateRoomDrawer__required-fields'>{`(*) ${drawer.requiredFields}`}</div>
      </main>
      <footer>
        <DefaultButton
          disabled={drawer.isLoading}
          onClick={drawer.onClose}
          title={drawer.closeButtonText} />
        <PrimaryButton
          disabled={drawer.isDisabledSaveButton}
          loading={drawer.isLoading}
          onClick={drawer.onSave}
          title={drawer.saveButtonText}
        />
      </footer>
    </DrawerAtom>
  );
}

export default CheckInCreateRoomDrawer;
